import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface Role {
  id: string;
  name: string;
  description: string;
  // Backend (RoleWithPermissionsDto) returns this as a flat list of permission names.
  permissions: string[];
  createdAt: string;
  isActive: boolean;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  permissionNames: string[];
}

export interface AssignPermissionsRequest {
  roleId: string;
  permissionNames: string[];
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export const rolesApi = {
  getAllRoles: async (pageNumber = 1, pageSize = 20, search?: string): Promise<PagedResult<Role>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<Role>>>('/Roles', {
      params: { pageNumber, pageSize, search },
    });
    return data.data;
  },

  getRoleById: async (id: string): Promise<Role> => {
    const { data } = await apiClient.get<ApiResponse<Role>>(`/Roles/${id}`);
    return data.data;
  },

  createRole: async (payload: CreateRoleRequest): Promise<Role> => {
    const { data } = await apiClient.post<ApiResponse<Role>>(
      '/Roles',
      payload
    );
    return data.data;
  },

  assignPermissions: async (
    id: string,
    payload: AssignPermissionsRequest
  ): Promise<void> => {
    await apiClient.post(`/Roles/${id}/permissions`, payload);
  },

  deleteRole: async (id: string): Promise<void> => {
    await apiClient.delete(`/Roles/${id}`);
  },

  // Get available permissions (hardcoded for now)
  getAvailablePermissions: async (): Promise<Permission[]> => {
    const { data } = await apiClient.get<ApiResponse<string[]>>('/Roles/permissions');
    return data.data.map((permissionString) => ({
      id: permissionString,
      name: permissionString,
      description: permissionString
    }));
  },
};